import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

type LazyPDFPageProps = {
  pageNumber: number;
  pageHeight: number | undefined;
  scale: number;
  isDocumentReady: boolean;
  onVisible?: (pageNumber: number) => void;
  children: ReactNode;
};

const ESTIMATED_PAGE_HEIGHT = 800;

export const LazyPDFPage = ({
  pageNumber,
  pageHeight,
  scale,
  isDocumentReady,
  onVisible,
  children,
}: LazyPDFPageProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const onVisibleRef = useRef(onVisible);
  onVisibleRef.current = onVisible;

  const handleVisibility = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          onVisibleRef.current?.(pageNumber);
        }
      }
    },
    [pageNumber],
  );

  useEffect(() => {
    const element = ref.current;

    if (!element) return;

    const renderObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNearViewport(true);
        }
      },
      { rootMargin: "200% 0px" },
    );

    const visibilityObserver = new IntersectionObserver(handleVisibility, {
      rootMargin: "-40% 0px -40% 0px",
      threshold: 0,
    });

    renderObserver.observe(element);
    visibilityObserver.observe(element);

    return () => {
      renderObserver.disconnect();
      visibilityObserver.disconnect();
    };
  }, [handleVisibility]);

  const shouldRender = isNearViewport && isDocumentReady;

  return (
    <div
      data-page-number={pageNumber}
      ref={ref}
      style={{
        minHeight: Math.round((pageHeight ?? ESTIMATED_PAGE_HEIGHT) * scale),
        margin: "0 auto",
      }}
    >
      {shouldRender ? children : null}
    </div>
  );
};
